import {
    Grid,
    Loader,
    PasswordInput,
    Space,
    Stack,
    Text,
    TextInput,
    ThemeIcon,
    Title,
} from '@mantine/core'
import {
    Check,
    PartyPopper,
    Mail,
    Frown,
    Phone,
    UserX,
} from 'lucide-react'
import { Controller } from 'react-hook-form'

import { RegisterDto } from '@/domain/Authentication/dtos/register.dto'
import { WithFormControl } from '@/shared/types/components'

type RegisterWithConfirmPassword = RegisterDto & { confirmPassword: string }

const General = ({ control }: WithFormControl<RegisterWithConfirmPassword>) => (
    <Grid.Col w="100%">
        <Stack spacing={0} mb="lg">
            <Title order={6}>Informations générales</Title>
            <Text size="sm" color="gray">
                Ces informations seront utilisées pour vous connecter à votre
                compte.
            </Text>
        </Stack>
        <Stack w="100%">
            <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                    <TextInput
                        autoFocus
                        label="E-mail"
                        autoComplete="off"
                        placeholder="ex. pierre-dupont@mail.com"
                        error={fieldState.error?.message}
                        value={field.value}
                        onChange={field.onChange}
                        icon={<Mail className="w-4 h-4" />}
                    />
                )}
            />
            <Controller
                control={control}
                name="password"
                render={({ field, fieldState }) => (
                    <PasswordInput
                        autoComplete="off"
                        label="Mot de passe"
                        placeholder="••••••••"
                        error={fieldState.error?.message}
                        value={field.value}
                        onChange={field.onChange}
                    />
                )}
            />
            <Controller
                control={control}
                name="confirmPassword"
                render={({ field, fieldState }) => (
                    <PasswordInput
                        autoComplete="off"
                        label="Confirmation du mot de passe"
                        placeholder="••••••••"
                        error={fieldState.error?.message}
                        value={field.value}
                        onChange={field.onChange}
                    />
                )}
            />
        </Stack>
    </Grid.Col>
)

const Personal = ({
    control,
}: WithFormControl<RegisterWithConfirmPassword>) => (
    <Grid.Col w="100%">
        <Stack spacing={0} mb="lg">
            <Title order={6}>Informations personnelles</Title>
            <Text size="sm" color="gray">
                Ces informations seront utilisées pour vos commandes et pour
                vous contacter.
            </Text>
        </Stack>
        <Stack w="100%">
            <Controller
                control={control}
                name="first_name"
                render={({ field, fieldState }) => (
                    <TextInput
                        autoComplete="off"
                        label="Prénom"
                        placeholder="ex. Pierre"
                        error={fieldState.error?.message}
                        value={field.value}
                        onChange={field.onChange}
                    />
                )}
            />
            <Controller
                control={control}
                name="last_name"
                render={({ field, fieldState }) => (
                    <TextInput
                        autoComplete="off"
                        label="Nom de famille"
                        placeholder="ex. Dupont"
                        error={fieldState.error?.message}
                        value={field.value}
                        onChange={field.onChange}
                    />
                )}
            />
            <Controller
                control={control}
                name="phone"
                render={({ field, fieldState }) => (
                    <TextInput
                        autoComplete="off"
                        label="Téléphone"
                        description="Optionnel"
                        icon={<Phone className="w-4 h-4" />}
                        placeholder="ex. 06 00 00 00 00"
                        error={fieldState.error?.message}
                        pattern="[0-9]{2} [0-9]{2} [0-9]{2} [0-9]{2} [0-9]{2}"
                        value={field.value || ''}
                        onChange={field.onChange}
                    />
                )}
            />
        </Stack>
    </Grid.Col>
)

const Processing = () => (
    <Grid.Col w="100%" className="flex items-center justify-center h-full">
        <Loader color="blue" />
    </Grid.Col>
)

const Success = () => {
    return (
        <Grid.Col className="h-full">
            <Stack
                className="items-center justify-center h-full text-center"
                spacing={'sm'}
            >
                <ThemeIcon
                    size={48}
                    radius={9999}
                    variant="light"
                    color="var(--primary)"
                >
                    <Check />
                </ThemeIcon>
                <Title order={5}>Votre compte a bien été créé !</Title>
                <Text className="text-sm lg:text-base" c="gray.6">
                    Bienvenue à bord, vous pouvez continuer votre commande en
                    cliquant ci-dessous.
                </Text>

                <Space h="sm" />
            </Stack>
        </Grid.Col>
    )
}

const Error = () => (
    <Grid.Col className="h-full">
        <Stack
            className="items-center justify-center h-full text-center"
            spacing={'sm'}
        >
            <ThemeIcon size={48} radius={9999} variant="light" color="red">
                <Frown />
            </ThemeIcon>
            <Title order={5}>Une erreur est survenue!</Title>
            <Text className="text-sm lg:text-base" c="gray.6">
                Vous pouvez réessayer l'opération ou contacter le support.
            </Text>

            <Space h="sm" />
        </Stack>
    </Grid.Col>
)

const ErrorUserAlreadyExists = () => (
    <Grid.Col className="h-full">
        <Stack
            className="items-center justify-center h-full text-center"
            spacing={'sm'}
        >
            <ThemeIcon color="red" size={48} radius={9999} variant="light">
                <UserX />
            </ThemeIcon>
            <Title order={5}>Vous avez déjà un compte stoux</Title>
            <Text className="text-sm lg:text-base" c="gray.6">
                Ça arrive, ne vous inquiétez pas. Cliquez ci-dessous pour vous
                connecter.
            </Text>

            <Space h="sm" />
        </Stack>
    </Grid.Col>
)
const Steps = {
    General,
    Personal,
    Processing,
    Success,
    Error,
    ErrorUserAlreadyExists,
}

export default Steps
